/*
 * THE 5TH NOTE FRAGRANCE DNA — engine (runs in the browser, no keys, no secrets)
 *
 * Fragrance DNA:  30 scores 0–10 per product, from the product metafield dna.scores
 *                 (panel-verified) or drafted from tags and notes when missing.
 * Customer DNA:   per characteristic a preference p, importance w, tolerance s and mode
 *                 (target | max | min), built from the questionnaire and ratings.
 * Matching:       direction-aware weighted distance  D = Σ W(Δ/σ)² / ΣW,  S = e^(−D/2)
 * Score:          R = Σ β·component over 8 components (weights from Theme settings).
 * Slots:          Signature, Safe bet, Next note, Discovery, Occasion, Wildcard, then more.
 * Learning:       ratings pull/push the DNA; questionnaire share = 6 / (6 + ratings).
 */
(function () {
  'use strict';

  /* ---------------- 1. The 30 characteristics ---------------- */
  const DIMS = ['freshness', 'sweetness', 'woody', 'spicy', 'smoky', 'creamy', 'citrus', 'floral', 'fruity', 'aquatic',
    'green', 'herbal', 'powdery', 'musky', 'amber', 'vanilla', 'oud', 'leather', 'tobacco', 'earthy',
    'warmth', 'dryness', 'cleanliness', 'projection', 'longevity', 'versatility', 'formality', 'seductiveness', 'complexity', 'uniqueness'];
  const N = DIMS.length;
  const I = Object.fromEntries(DIMS.map((d, i) => [d, i]));
  const LABEL = Object.fromEntries(DIMS.map((d) => [d, d.charAt(0).toUpperCase() + d.slice(1)]));
  const CHARACTER = DIMS.slice(0, 20);
  const ADJ = {
    freshness: 'fresh', sweetness: 'sweet', woody: 'woody', spicy: 'spicy', smoky: 'smoky', creamy: 'creamy', citrus: 'citrus',
    floral: 'floral', fruity: 'fruity', aquatic: 'aquatic', green: 'green', herbal: 'aromatic', powdery: 'powdery', musky: 'musky',
    amber: 'amber', vanilla: 'vanilla', oud: 'oud', leather: 'leather', tobacco: 'tobacco', earthy: 'earthy', warmth: 'warm',
    dryness: 'dry', cleanliness: 'clean', projection: 'strong-projecting', longevity: 'long-lasting', versatility: 'versatile',
    formality: 'formal', seductiveness: 'seductive', complexity: 'complex', uniqueness: 'unusual'
  };

  /* ---------------- 2. Note and family map (rule draft) ---------------- */
  const NOTE_MAP = {
    // families (match the questionnaire wording)
    'woody': { woody: 1.6, dryness: 0.4 }, 'fresh': { freshness: 1.6, cleanliness: 0.6 },
    'amber & sweet': { amber: 1.2, sweetness: 1.2, vanilla: 0.5, warmth: 0.8 }, 'aromatic': { herbal: 1.5, freshness: 0.6 },
    'citrus': { citrus: 1.6, freshness: 1.0 }, 'spicy': { spicy: 1.6, warmth: 0.6 }, 'leather & smoke': { leather: 1.2, smoky: 1.2, warmth: 0.4 },
    'aquatic': { aquatic: 1.6, freshness: 1.0, cleanliness: 0.5 }, 'floral': { floral: 1.6 }, 'fruity': { fruity: 1.6, sweetness: 0.5 },
    'gourmand': { sweetness: 1.6, vanilla: 0.8, warmth: 0.6 }, 'oriental': { amber: 1.3, spicy: 0.8, warmth: 1.0 }, 'green': { green: 1.6, freshness: 0.6 },
    'earthy': { earthy: 1.6 }, 'smoky': { smoky: 1.6 }, 'powdery': { powdery: 1.6 }, 'musky': { musky: 1.6, cleanliness: 0.5 },
    // notes
    'oud': { oud: 1.6, woody: 0.6, warmth: 0.5 }, 'agarwood': { oud: 1.6, woody: 0.6 }, 'vanilla': { vanilla: 1.6, sweetness: 1.0, warmth: 0.6, creamy: 0.3 },
    'bergamot': { citrus: 1.2, freshness: 0.8 }, 'sandalwood': { woody: 1.0, creamy: 1.0, warmth: 0.3 }, 'vetiver': { woody: 0.9, earthy: 0.9, dryness: 0.6, green: 0.3 },
    'lavender': { herbal: 1.3, floral: 0.4, cleanliness: 0.4 }, 'tobacco': { tobacco: 1.6, warmth: 0.6, sweetness: 0.3 }, 'leather': { leather: 1.6, smoky: 0.3 },
    'amber': { amber: 1.5, warmth: 0.8, sweetness: 0.4 }, 'tonka': { sweetness: 0.9, vanilla: 0.6, powdery: 0.4, warmth: 0.4 }, 'tonka bean': { sweetness: 0.9, vanilla: 0.6, powdery: 0.4, warmth: 0.4 },
    'cedar': { woody: 1.3, dryness: 0.6 }, 'cedarwood': { woody: 1.3, dryness: 0.6 }, 'black pepper': { spicy: 1.3, freshness: 0.2 }, 'pepper': { spicy: 1.2 },
    'pink pepper': { spicy: 0.9, freshness: 0.4 }, 'musk': { musky: 1.5, cleanliness: 0.6 }, 'white musk': { musky: 1.4, cleanliness: 1.0 },
    'grapefruit': { citrus: 1.3, freshness: 0.9 }, 'mint': { herbal: 1.0, freshness: 1.2, green: 0.3 }, 'rose': { floral: 1.5 },
    'coffee': { sweetness: 0.5, warmth: 0.5, earthy: 0.3, smoky: 0.2 }, 'incense': { smoky: 1.4, amber: 0.4, warmth: 0.4 },
    'lemon': { citrus: 1.4, freshness: 0.9 }, 'orange': { citrus: 1.2, fruity: 0.3 }, 'mandarin': { citrus: 1.2, fruity: 0.3, sweetness: 0.2 },
    'neroli': { floral: 0.8, citrus: 0.6, cleanliness: 0.4 }, 'orange blossom': { floral: 1.3, sweetness: 0.3 }, 'jasmine': { floral: 1.5, seductiveness: 0.4 },
    'iris': { powdery: 1.4, floral: 0.6 }, 'orris': { powdery: 1.4, floral: 0.6 }, 'violet': { powdery: 0.9, floral: 0.8 }, 'violet leaf': { green: 1.2, freshness: 0.4 },
    'patchouli': { earthy: 1.4, woody: 0.4, warmth: 0.3 }, 'oakmoss': { earthy: 1.2, green: 0.5 }, 'moss': { earthy: 1.1, green: 0.5 },
    'cardamom': { spicy: 1.0, freshness: 0.4 }, 'cinnamon': { spicy: 1.3, warmth: 0.8, sweetness: 0.3 }, 'saffron': { spicy: 1.0, leather: 0.4, warmth: 0.4 },
    'ginger': { spicy: 1.0, freshness: 0.6 }, 'clove': { spicy: 1.3, warmth: 0.6 }, 'nutmeg': { spicy: 1.0, warmth: 0.5 },
    'apple': { fruity: 1.3, freshness: 0.4, sweetness: 0.3 }, 'pineapple': { fruity: 1.4, sweetness: 0.4 }, 'blackcurrant': { fruity: 1.2 },
    'plum': { fruity: 1.0, sweetness: 0.5 }, 'fig': { fruity: 0.8, green: 0.6, creamy: 0.4 }, 'coconut': { creamy: 1.3, sweetness: 0.5 },
    'honey': { sweetness: 1.3, warmth: 0.4 }, 'caramel': { sweetness: 1.6, warmth: 0.5 }, 'chocolate': { sweetness: 1.0, warmth: 0.5 }, 'cacao': { sweetness: 0.8, warmth: 0.5 },
    'praline': { sweetness: 1.5 }, 'benzoin': { amber: 1.2, vanilla: 0.5, sweetness: 0.4 }, 'labdanum': { amber: 1.4, leather: 0.3 },
    'myrrh': { amber: 0.9, smoky: 0.4 }, 'ambroxan': { woody: 0.8, musky: 0.8, amber: 0.4, cleanliness: 0.3 }, 'ambergris': { musky: 0.9, aquatic: 0.4, amber: 0.4 },
    'sea salt': { aquatic: 1.2, freshness: 0.6 }, 'sea notes': { aquatic: 1.5, freshness: 0.8 }, 'marine': { aquatic: 1.5, freshness: 0.8 }, 'ozonic': { aquatic: 1.0, freshness: 1.0 },
    'basil': { herbal: 1.0, green: 0.5 }, 'sage': { herbal: 1.2 }, 'clary sage': { herbal: 1.2 }, 'rosemary': { herbal: 1.2, freshness: 0.4 },
    'geranium': { floral: 0.8, herbal: 0.6 }, 'green notes': { green: 1.5, freshness: 0.5 }, 'grass': { green: 1.4 }, 'galbanum': { green: 1.5 },
    'birch': { leather: 1.0, smoky: 1.0 }, 'guaiac wood': { woody: 1.0, smoky: 0.6 }, 'suede': { leather: 1.2, powdery: 0.4, creamy: 0.3 },
    'aldehydes': { freshness: 0.8, cleanliness: 1.0, powdery: 0.4 }, 'cashmeran': { musky: 0.6, woody: 0.6, powdery: 0.4 },
    'cypress': { woody: 0.9, green: 0.6, freshness: 0.4 }, 'pine': { woody: 0.8, green: 0.8, freshness: 0.6 }, 'juniper': { herbal: 0.7, freshness: 0.7, green: 0.4 },
    'elemi': { citrus: 0.5, spicy: 0.5, freshness: 0.4 }, 'rum': { sweetness: 0.8, warmth: 0.7 }, 'whisky': { warmth: 0.8, woody: 0.4, sweetness: 0.4 },
    'cherry': { fruity: 1.2, sweetness: 0.8 }, 'peach': { fruity: 1.2, sweetness: 0.5, creamy: 0.3 }, 'pear': { fruity: 1.2, freshness: 0.4 },
    'tea': { green: 0.8, freshness: 0.6, dryness: 0.4 }, 'heliotrope': { powdery: 1.2, sweetness: 0.4 }, 'tuberose': { floral: 1.5, creamy: 0.6, seductiveness: 0.5 }
  };
  const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

  /* ---------------- 3. Occasions and personality (editable tables) ---------------- */
  // m: importance multipliers, t: blended targets (30%), max/min: soft limits
  const OCCASIONS = {
    'everyday': { label: 'Everyday', m: { versatility: 2, cleanliness: 1.5, uniqueness: 0.6 }, max: { projection: 7 } },
    'work': { label: 'Work', m: { cleanliness: 2, versatility: 1.8, freshness: 1.5, seductiveness: 0.5, oud: 0.5, smoky: 0.5 }, max: { projection: 6 } },
    'business meeting': { label: 'Business meeting', m: { formality: 2, cleanliness: 1.8, woody: 1.5, sweetness: 0.5, fruity: 0.5 }, max: { projection: 6 }, t: { formality: 7 } },
    'interview': { label: 'Interview', m: { cleanliness: 2.2, formality: 1.8, seductiveness: 0.4, uniqueness: 0.5, sweetness: 0.5 }, max: { projection: 5 }, t: { cleanliness: 8 } },
    'first date': { label: 'First date', m: { seductiveness: 2, cleanliness: 1.6, musky: 1.5, smoky: 0.5, oud: 0.5, leather: 0.5 }, min: { projection: 4 }, max: { projection: 7 } },
    'date night': { label: 'Date night', m: { seductiveness: 2.4, warmth: 1.8, amber: 1.5, vanilla: 1.5, aquatic: 0.5, herbal: 0.6 }, min: { projection: 5 }, t: { seductiveness: 8, warmth: 7 } },
    'wedding': { label: 'Wedding', m: { formality: 2.2, longevity: 2, woody: 1.5, amber: 1.5, complexity: 1.5, aquatic: 0.5, fruity: 0.5 }, min: { longevity: 7 }, t: { formality: 8 } },
    'formal event': { label: 'Formal event', m: { formality: 2.4, complexity: 1.8, longevity: 1.8, aquatic: 0.5, citrus: 0.6 }, min: { longevity: 7 }, t: { formality: 8 } },
    'night out': { label: 'Night out', m: { projection: 2, seductiveness: 2, warmth: 1.5, herbal: 0.6, green: 0.6 }, min: { projection: 6 } },
    'club': { label: 'Club', m: { projection: 2.4, sweetness: 1.5, longevity: 1.8, powdery: 0.5, green: 0.5 }, min: { projection: 7 } },
    'party': { label: 'Party', m: { projection: 2, seductiveness: 1.8, fruity: 1.4, earthy: 0.6 }, min: { projection: 6 } },
    'dinner': { label: 'Dinner', m: { seductiveness: 1.8, warmth: 1.6, complexity: 1.5, aquatic: 0.6 }, max: { projection: 7 } },
    'birthday': { label: 'Birthday', m: { seductiveness: 1.6, uniqueness: 1.6 } },
    'gym': { label: 'Gym', m: { freshness: 2.4, cleanliness: 2.2, aquatic: 1.8, citrus: 1.8, versatility: 1.5, oud: 0.4, leather: 0.4, smoky: 0.4, sweetness: 0.5, warmth: 0.5 }, max: { projection: 5, sweetness: 4, warmth: 4 }, t: { freshness: 9, cleanliness: 8 } },
    'holiday': { label: 'Holiday', m: { freshness: 1.8, citrus: 1.6, fruity: 1.4, formality: 0.5 } },
    'beach': { label: 'Beach', m: { aquatic: 2.2, freshness: 2, citrus: 1.6, warmth: 0.5, smoky: 0.4, oud: 0.4, leather: 0.4 }, max: { warmth: 4 }, t: { aquatic: 7, freshness: 8 } },
    'travel': { label: 'Travel', m: { versatility: 2, cleanliness: 1.6, longevity: 1.6, uniqueness: 0.6 }, max: { projection: 6 } },
    'summer': { label: 'Summer', m: { freshness: 1.8, citrus: 1.6, aquatic: 1.6, warmth: 0.6, amber: 0.6, vanilla: 0.6 }, max: { warmth: 5 } },
    'winter': { label: 'Winter', m: { warmth: 1.8, amber: 1.6, spicy: 1.5, longevity: 1.5, aquatic: 0.6 } }
  };
  const OCCASION_ALIASES = { 'every day': 'everyday', 'office': 'work', 'nights out': 'night out', 'dates': 'date night', 'special occasions': 'formal event' };
  const occasionKey = (o) => { const k = norm(o); return OCCASIONS[k] ? k : (OCCASION_ALIASES[k] || null); };

  // nudge: target shift (points, before averaging), m: importance multiplier
  const TRAITS = {
    'expensive': { up: ['complexity', 'woody', 'amber', 'uniqueness'], down: ['sweetness', 'fruity'] },
    'clean': { up: ['cleanliness', 'freshness', 'musky'], down: ['smoky', 'sweetness', 'earthy'] },
    'sexy': { up: ['seductiveness', 'warmth', 'amber', 'vanilla', 'musky'], down: ['herbal', 'green'] },
    'powerful': { up: ['projection', 'longevity', 'oud', 'leather'], down: ['aquatic', 'powdery'] },
    'mysterious': { up: ['smoky', 'oud', 'earthy', 'complexity', 'uniqueness'], down: ['citrus', 'fruity', 'cleanliness'] },
    'sophisticated': { up: ['formality', 'complexity', 'powdery', 'woody'], down: ['sweetness', 'fruity'] },
    'confident': { up: ['projection', 'woody', 'spicy'], down: ['powdery'] },
    'approachable': { up: ['cleanliness', 'freshness'], down: ['oud', 'smoky'], max: { projection: 6 } },
    'unique': { up: ['uniqueness', 'complexity'], down: ['versatility'] },
    'elegant': { up: ['formality', 'floral', 'powdery', 'musky'], down: ['fruity', 'aquatic'] },
    'relaxed': { up: ['freshness', 'herbal', 'green'], down: ['formality'], max: { projection: 6 } },
    'energetic': { up: ['citrus', 'freshness', 'fruity', 'spicy'], down: ['warmth', 'amber'] },
    'creative': { up: ['uniqueness', 'green', 'earthy', 'complexity'], down: ['versatility'] }
  };

  // South African seasons: Dec–Feb summer, Jun–Aug winter
  const season = (date) => {
    const m = (date || new Date()).getMonth();
    if (m === 11 || m <= 1) return 'summer';
    if (m >= 5 && m <= 7) return 'winter';
    return m <= 4 ? 'autumn' : 'spring';
  };
  const SEASON_WARMTH = { summer: 3.5, autumn: 5.5, winter: 7, spring: 4.5 };

  /* ---------------- 4. Config (weights from Theme settings) ---------------- */
  const DEFAULT_WEIGHTS = { dna: 35, explicit: 20, feedback: 15, occasion: 10, season: 5, personality: 5, inventory: 5, discovery: 5 };
  const cfg = () => {
    const c = window.FifthDNAConfig || {};
    const w = Object.assign({}, DEFAULT_WEIGHTS, c.weights || {});
    const sum = Object.values(w).reduce((a, b) => a + Number(b || 0), 0) || 1;
    Object.keys(w).forEach((k) => (w[k] = Number(w[k] || 0) / sum));
    return { weights: w, aiUrl: c.aiUrl || '', sheetUrl: c.sheetUrl || '', sheetKey: c.sheetKey || '', customerId: c.customerId || '' };
  };

  /* ---------------- 5. Product DNA ---------------- */
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const squash = (raw) => 10 * (1 - Math.exp(-0.6 * raw));

  function parseScores(raw) {
    if (raw == null || raw === '') return null;
    let obj = raw;
    if (typeof raw === 'string') {
      const t = raw.trim();
      if (t.startsWith('{')) { try { obj = JSON.parse(t); } catch (_) { return null; } }
      else {
        const nums = t.split(/[,;\s]+/).filter(Boolean).map(Number);
        if (nums.length < N || nums.some((x) => isNaN(x))) return null;
        return nums.slice(0, N).map((x) => clamp(x, 0, 10));
      }
    }
    if (typeof obj !== 'object') return null;
    const v = DIMS.map((d) => (obj[d] != null ? clamp(Number(obj[d]), 0, 10) : NaN));
    return v.some((x) => isNaN(x)) ? null : v;
  }

  function termsOf(p) {
    const tags = (p.tags || []).map(norm);
    const notes = [p.notes_top, p.notes_heart, p.notes_base].filter(Boolean).join(',').split(',').map(norm).filter(Boolean);
    return Array.from(new Set(tags.concat(notes)));
  }

  function draftDNA(terms) {
    const raw = new Array(N).fill(0);
    terms.forEach((t) => {
      const m = NOTE_MAP[t];
      if (m) Object.keys(m).forEach((d) => { if (I[d] != null) raw[I[d]] += m[d]; });
    });
    const v = raw.map(squash);
    const g = (d) => v[I[d]];
    // derived characteristics from the character scores
    v[I.warmth] = clamp(5 + 0.35 * (g('amber') + g('vanilla') + g('spicy') + g('oud') + g('tobacco') + g('leather')) / 2 - 0.4 * (g('freshness') + g('aquatic') + g('citrus')) / 1.5, 0, 10);
    v[I.freshness] = Math.max(g('freshness'), 0.6 * Math.max(g('citrus'), g('aquatic'), g('herbal'), g('green')));
    v[I.sweetness] = Math.max(g('sweetness'), 0.6 * Math.max(g('vanilla'), g('fruity')));
    v[I.dryness] = clamp(5 + 0.3 * (g('woody') + g('earthy') + g('smoky')) / 1.5 - 0.6 * g('sweetness') - 0.2 * g('creamy'), 0, 10);
    v[I.cleanliness] = clamp(Math.max(raw[I.cleanliness] ? squash(raw[I.cleanliness]) : 0, 3 + 0.25 * (g('musky') + g('freshness') + g('aquatic') + g('citrus')) / 2 - 0.3 * (g('oud') + g('leather') + g('tobacco') + g('smoky')) / 2), 0, 10);
    v[I.projection] = clamp(4.5 + 0.12 * (g('oud') + g('amber') + g('sweetness') + g('spicy') + g('leather')) / 1.2 - 0.1 * (g('musky') + g('citrus')), 0, 10);
    v[I.longevity] = clamp(4.5 + 0.15 * (g('oud') + g('amber') + g('vanilla') + g('woody') + g('leather') + g('tobacco')) / 1.5 - 0.15 * (g('citrus') + g('aquatic')), 0, 10);
    v[I.versatility] = clamp(5 + 0.25 * (g('freshness') + g('cleanliness')) / 2 - 0.25 * (g('oud') + g('smoky') + g('leather') + g('sweetness')) / 2, 0, 10);
    v[I.formality] = clamp(4 + 0.3 * (g('woody') + g('powdery') + g('amber') + g('leather')) / 2 - 0.25 * (g('aquatic') + g('fruity')) / 1.5, 0, 10);
    v[I.seductiveness] = clamp(3 + 0.35 * (g('vanilla') + g('amber') + g('musky') + g('warmth')) / 2, 0, 10);
    const facets = CHARACTER.filter((d) => g(d) >= 3).length;
    v[I.complexity] = clamp(1.5 + facets * 0.9, 0, 10);
    v[I.uniqueness] = 5; // replaced by the neighbourhood measure once the catalogue is loaded
    return v;
  }

  function prepareProduct(p) {
    const terms = termsOf(p);
    const scored = parseScores(p.dna);
    const dupe = terms.includes('dupe') || terms.includes('dupes');
    const conf = scored ? (p.verified === false ? 0.6 : 0.9) : 0.4;
    const v = scored || draftDNA(terms);
    const hasData = !!scored || terms.some((t) => NOTE_MAP[t]);
    // per-characteristic trust: drafted performance and context scores are rough guesses
    const cd = DIMS.map((d, i) => (scored ? conf : i < 20 ? 0.6 : (d === 'projection' || d === 'longevity') ? 0.1 : i < 23 ? 0.45 : 0.3));
    return Object.assign({}, p, { v, conf, cd, source: scored ? 'scored' : 'draft', terms, dupe, hasData, uniquenessSet: !!scored });
  }

  function computeUniqueness(list) {
    const pool = list.filter((p) => p.hasData);
    if (pool.length < 12 || pool.length > 4000) return;
    pool.forEach((p) => {
      if (p.uniquenessSet) return;
      const ds = [];
      for (const q of pool) {
        if (q === p) continue;
        let s = 0;
        for (let i = 0; i < 20; i++) { const d = p.v[i] - q.v[i]; s += d * d; }
        ds.push(Math.sqrt(s));
      }
      ds.sort((a, b) => a - b);
      const k = ds.slice(0, 10);
      const avg = k.reduce((a, b) => a + b, 0) / k.length;
      p.v[I.uniqueness] = clamp(avg * 1.1, 0, 10);
    });
  }

  /* ---------------- 6. Catalogue loading (theme feed: /collections/all?view=dna) ---------------- */
  let catalogueCache = null;
  async function loadCatalogue() {
    if (catalogueCache) return catalogueCache;
    const KEY = '5n_dna_catalogue_v1';
    try {
      const hit = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (hit && Date.now() - hit.at < 15 * 60 * 1000) return (catalogueCache = finish(hit.products));
    } catch (_) {}
    const get = (page) => fetch(`/collections/all?view=dna&page=${page}`, { credentials: 'same-origin' }).then((r) => (r.ok ? r.json() : { products: [], pages: 0 }));
    const first = await get(1);
    let products = first.products || [];
    const pages = Math.min(first.pages || 1, 400);
    for (let start = 2; start <= pages; start += 6) {
      const batch = [];
      for (let p = start; p < start + 6 && p <= pages; p++) batch.push(get(p));
      (await Promise.all(batch)).forEach((r) => (products = products.concat(r.products || [])));
    }
    try { sessionStorage.setItem(KEY, JSON.stringify({ at: Date.now(), products })); } catch (_) {}
    return (catalogueCache = finish(products));

    function finish(raw) {
      const list = raw.map(prepareProduct);
      computeUniqueness(list);
      const byHandle = Object.fromEntries(list.map((p) => [p.handle, p]));
      return { list, byHandle, analysed: list.filter((p) => p.hasData).length };
    }
  }

  /* ---------------- 7. Customer DNA ---------------- */
  const AVOID_WORDS = {
    sweet: 'sweetness', sugary: 'sweetness', gourmand: 'sweetness', floral: 'floral', flowery: 'floral', fruity: 'fruity', smoky: 'smoky', smoke: 'smoky',
    oud: 'oud', leather: 'leather', vanilla: 'vanilla', powdery: 'powdery', musky: 'musky', spicy: 'spicy', woody: 'woody', citrus: 'citrus',
    aquatic: 'aquatic', marine: 'aquatic', tobacco: 'tobacco', earthy: 'earthy', strong: 'projection', overpowering: 'projection', loud: 'projection', heavy: 'warmth'
  };

  function blankAcc() { return DIMS.map(() => ({ sx: 0, s: 0, imp: 0, xs: [] })); }
  // imp: how much this evidence says the characteristic MATTERS (a faint facet in a loved fragrance matters little)
  function add(acc, d, x, s, imp) { const a = acc[I[d]]; a.sx += x * s; a.s += s; a.imp += s * (imp == null ? 1 : imp); a.xs.push(x); }

  function questionnaireDNA(ans, cat) {
    const acc = blankAcc();
    const mode = new Array(N).fill('target');
    const imp = new Array(N).fill(null);
    const caps = {}; // hard caps from hated notes / characteristics
    const hardNotes = (ans.hate || []).map(norm);
    const liked = (ans.families || []).concat(ans.notes || []).concat(String(ans.other_notes || '').split(',')).map(norm).filter(Boolean);

    liked.forEach((t) => {
      const m = NOTE_MAP[t];
      if (!m) return;
      Object.keys(m).forEach((d) => { if (I[d] == null) return; const w = m[d]; add(acc, d, w >= 1.2 ? 7.8 : 6.5, 0.7 * Math.min(1, w)); });
    });
    (ans.loved || []).forEach((h) => {
      const p = cat && cat.byHandle[h];
      if (p) DIMS.forEach((d, i) => add(acc, d, p.v[i], 1.0 * p.conf, i < 20 ? 0.15 + 0.85 * Math.pow(p.v[i] / 10, 2) : 0.4));
    });
    const strength = { 'close to the skin': [3, 'max', 1.6], 'noticeable': [6, 'target', 1.2], 'fills the room': [8, 'min', 2] }[norm(ans.strength)];
    if (strength) { add(acc, 'projection', strength[0], 1); mode[I.projection] = strength[1]; imp[I.projection] = strength[2]; }
    const lasting = { 'a few hours': [5, 'min', 1.2], 'most of the day': [7, 'min', 1.8], 'all day and night': [9, 'min', 2.5] }[norm(ans.lasting)];
    if (lasting) { add(acc, 'longevity', lasting[0], 1); mode[I.longevity] = lasting[1]; imp[I.longevity] = lasting[2]; }

    // hated notes: their main characteristic becomes mode "max" with a hard cap
    hardNotes.forEach((n) => {
      const m = NOTE_MAP[n];
      if (!m) return;
      Object.keys(m).filter((d) => m[d] >= 1.2 && I[d] != null).forEach((d) => {
        add(acc, d, 1.5, 1.2); mode[I[d]] = 'max'; imp[I[d]] = 2.6; caps[d] = Math.min(caps[d] == null ? 10 : caps[d], 4);
      });
    });
    // "anything you don't want?" free text
    const avoid = norm(ans.avoid);
    Object.keys(AVOID_WORDS).forEach((w) => {
      if (!new RegExp('\\b' + w).test(avoid)) return;
      const d = AVOID_WORDS[w];
      const x = d === 'projection' ? 5 : 2;
      add(acc, d, x, 1.0); mode[I[d]] = 'max'; imp[I[d]] = Math.max(imp[I[d]] || 0, 2.2);
    });

    const p = acc.map((a) => (a.s > 0 ? a.sx / a.s : 5));
    const w = acc.map((a, i) => (imp[i] != null ? imp[i] : a.s > 0 ? clamp(0.3 + a.imp * 0.7, 0.3, 2.4) : 0.3));
    const sig = acc.map((a) => {
      if (a.imp < 0.5) return 2.4;
      if (a.xs.length < 2) return 1.8;
      const m = a.xs.reduce((x, y) => x + y, 0) / a.xs.length;
      const sd = Math.sqrt(a.xs.reduce((x, y) => x + (y - m) * (y - m), 0) / a.xs.length);
      return clamp(sd * 1.2, 1.2, 3);
    });

    // personality: averaged nudges (±1.5 cap), plus importance multipliers
    const traits = (ans.traits || []).map(norm).filter((t) => TRAITS[t]).slice(0, 3);
    const pm = new Array(N).fill(1);
    if (traits.length) {
      const shift = new Array(N).fill(0);
      traits.forEach((t) => {
        TRAITS[t].up.forEach((d) => { shift[I[d]] += 1.5; pm[I[d]] *= 1.25; });
        TRAITS[t].down.forEach((d) => { shift[I[d]] -= 1.5; pm[I[d]] *= 1.1; });
        Object.entries(TRAITS[t].max || {}).forEach(([d, v]) => { if (mode[I[d]] === 'target' && p[I[d]] > v) p[I[d]] = v; });
      });
      shift.forEach((s, i) => { p[i] = clamp(p[i] + clamp((s / traits.length) * 0.4, -1.5, 1.5), 0, 10); });
    }
    const evidence = acc.map((a) => a.imp);
    return { p, w, sig, mode, caps, hardNotes, liked, traits, pm, evidence };
  }

  const RATING = { loved: 1.0, liked: 0.5, okay: 0, not_for_me: -0.6, hated: -1.0 };
  const COMMENT_SIGNALS = [
    [/too sweet|so sweet|sugary/, 'sweetness', -1], [/not sweet enough|could be sweeter/, 'sweetness', 1],
    [/(doesn'?t|does not|didn'?t|did not) last|fades (fast|quick)|weak longevity|short.?lived/, 'longevity', 1],
    [/too strong|overpowering|too loud|headache/, 'projection', -1], [/too weak|too light|can'?t smell|too soft|skin scent/, 'projection', 1],
    [/too fresh|too clean|soapy/, 'freshness', -1], [/too floral|flowery/, 'floral', -1], [/too smoky/, 'smoky', -1],
    [/too woody/, 'woody', -1], [/too spicy/, 'spicy', -1], [/too powdery/, 'powdery', -1], [/too fruity/, 'fruity', -1],
    [/too heavy|too warm/, 'warmth', -1], [/too musky/, 'musky', -1], [/too old|old.?man|dated/, 'formality', -1]
  ];
  const commentSignals = (text) => {
    const t = norm(text);
    return COMMENT_SIGNALS.filter(([re]) => re.test(t)).map(([, d, dir]) => ({ d, dir }));
  };

  function buildCustomer(state, cat) {
    const Q = questionnaireDNA(state.answers || {}, cat);
    const B = Q.p.slice();
    const n = new Array(N).fill(0);
    const mode = Q.mode.slice();
    const w = Q.w.slice();
    const excluded = new Set(state.excluded || []);
    const lovedVecs = [];
    const dislikedVecs = [];
    (state.answers && state.answers.loved || []).forEach((h) => { const p = cat && cat.byHandle[h]; if (p) lovedVecs.push(p.v); });

    (state.ratings || []).forEach((r) => {
      // library fragrances (not sold in the store) carry their own trait vector
      const f = (cat && cat.byHandle[r.handle]) || (Array.isArray(r.v) && r.v.length === N ? { v: r.v, conf: 0.6 } : null);
      const sig = RATING[r.rating] != null ? RATING[r.rating] : 0;
      // smell stars (1–5) nudge the signal: a beautiful dry-down counts for more than a so-so opening
      const sm = [r.scent, r.drydown, r.opening].filter((x) => x);
      const smell = sm.length ? ((r.scent || 3) * 0.45 + (r.drydown || r.scent || 3) * 0.35 + (r.opening || r.scent || 3) * 0.2 - 3) * 0.12 : 0;
      const extra = (r.purchase ? 0.4 : 0) + (r.wear_again === false ? -0.2 : 0) + smell;
      const s = clamp(sig + extra, -1.2, 1.4);
      if (r.rating === 'hated') excluded.add(r.handle);
      if (f) {
        if (s > 0.3) lovedVecs.push(f.v); else if (s < -0.3) dislikedVecs.push(f.v);
        const diffs = f.v.map((x, i) => Math.abs(x - B[i]));
        const maxd = Math.max(...diffs) || 1;
        const named = new Set(commentSignals(r.comment).map((c) => c.d));
        f.v.forEach((x, i) => {
          const a = named.has(DIMS[i]) ? 1 : 0.3 + 0.7 * (diffs[i] / maxd);
          B[i] = clamp(B[i] + 0.15 * s * f.conf * a * (x - B[i]), 0, 10);
          n[i] += Math.abs(s) * a;
        });
        // performance ratings (1–5) compare with the fragrance's own scores
        if (r.longevity && r.longevity <= 2 && s >= 0) { const L = I.longevity; B[L] = Math.max(B[L], f.v[L] + 1); mode[L] = 'min'; w[L] = Math.max(w[L], 2); n[L] += 1; }
        if (r.projection && r.projection <= 2 && s >= 0) { const P = I.projection; B[P] = Math.max(B[P], f.v[P] + 1); mode[P] = mode[P] === 'max' ? 'target' : 'min'; n[P] += 1; }
        if (r.projection && r.projection >= 5 && s < 0) { const P = I.projection; B[P] = Math.min(B[P], f.v[P] - 1.5); mode[P] = 'max'; n[P] += 1; }
      }
      commentSignals(r.comment).forEach(({ d, dir }) => {
        const i = I[d];
        const ref = f ? f.v[i] : B[i];
        if (dir < 0) { B[i] = clamp(Math.min(B[i], ref - 1.5), 0, 10); if (mode[i] === 'target') mode[i] = 'max'; }
        else { B[i] = clamp(Math.max(B[i], ref + 1), 0, 10); if (mode[i] === 'target') mode[i] = 'min'; }
        w[i] = Math.max(w[i], 1.8); n[i] += 2;
      });
    });

    const p = Q.p.map((q, i) => { const alpha = 6 / (6 + n[i]); return clamp(alpha * q + (1 - alpha) * B[i], 0, 10); });
    const answers = state.answers || {};
    return {
      p, w, sig: Q.sig, mode, caps: Q.caps, hardNotes: Q.hardNotes, liked: Q.liked, traits: Q.traits, pm: Q.pm,
      evidence: Q.evidence.map((e, i) => e + n[i]), n, excluded, lovedVecs, dislikedVecs,
      occasions: (answers.occasions || []).map(occasionKey).filter(Boolean),
      adventure: answers.adventure != null ? Number(answers.adventure) : 5,
      budget: Number(answers.budget) || 0, kind: answers.kind || '',
      ratingsCount: (state.ratings || []).length
    };
  }

  /* ---------------- 8. Matching ---------------- */
  function dnaSimilarity(f, c, occ) {
    const o = occ ? OCCASIONS[occ] : null;
    let num = 0, den = 0;
    const contrib = [];
    const sea = season();
    for (let i = 0; i < N; i++) {
      const d = DIMS[i];
      let pd = c.p[i];
      let md = c.mode[i];
      if (o && o.t && o.t[d] != null) pd = 0.7 * pd + 0.3 * o.t[d];
      let gap;
      if (md === 'max') gap = Math.max(0, f.v[i] - pd);
      else if (md === 'min') gap = Math.max(0, pd - f.v[i]);
      else gap = Math.abs(f.v[i] - pd);
      if (o && o.max && o.max[d] != null) gap = Math.max(gap, f.v[i] - o.max[d]);
      if (o && o.min && o.min[d] != null) gap = Math.max(gap, o.min[d] - f.v[i]);
      let W = c.w[i] * (c.pm[i] || 1);
      if (o && o.m && o.m[d]) W *= o.m[d];
      if (!o && c.occasions.length) {
        const avg = c.occasions.reduce((s, k) => s + ((OCCASIONS[k].m || {})[d] || 1), 0) / c.occasions.length;
        W *= 1 + (avg - 1) * 0.5;
      }
      if ((d === 'warmth' || d === 'freshness') && (sea === 'summer' || sea === 'winter')) W *= 1.15;
      const trust = f.cd ? f.cd[i] : (f.conf || 1);
      const pen = W * trust * Math.pow(gap / c.sig[i], 2);
      num += pen; den += W;
      contrib.push({ d, i, gap, W, pen, f: f.v[i], p: pd, mode: md });
    }
    const D = den ? num / den : 0;
    return { S: Math.exp(-D / 2), contrib };
  }

  const vecSim = (a, b) => {
    let s = 0;
    for (let i = 0; i < 20; i++) { const d = a[i] - b[i]; s += d * d; }
    return Math.exp(-(s / 20) / (2 * 1.5 * 1.5));
  };

  function hardFiltered(f, c, ctx) {
    if (!f.available) return 'unavailable';
    if (c.excluded.has(f.handle)) return 'never again';
    if (ctx.budget && f.price > ctx.budget) return 'over budget';
    if (ctx.kind === 'Dupes' && !f.dupe) return 'not a dupe';
    if (ctx.kind === 'Genuine colognes' && f.dupe) return 'dupe';
    if (f.terms.includes('club-exclude')) return 'excluded';
    if (ctx.excludeHandles && ctx.excludeHandles.includes(f.handle)) return 'excluded';
    for (const n of c.hardNotes) {
      if (f.terms.includes(n)) return 'hard no: ' + n;
      const m = NOTE_MAP[n];
      if (m) for (const d of Object.keys(m)) if (m[d] >= 1.4 && I[d] != null && f.v[I[d]] >= 4) return 'hard no: ' + n;
    }
    for (const d of Object.keys(c.caps)) if (f.v[I[d]] > c.caps[d]) return 'hard no: ' + d;
    return '';
  }

  function components(f, c, ctx, sim, W) {
    W = W || cfg().weights;
    const likedHits = c.liked.filter((t) => f.terms.includes(t));
    const explicit = clamp(0.5 + 0.12 * likedHits.length, 0, 1);
    let feedback = 0.5;
    if (c.lovedVecs.length || c.dislikedVecs.length) {
      const lv = c.lovedVecs.length ? Math.max(...c.lovedVecs.map((v) => vecSim(f.v, v))) : 0;
      const dv = c.dislikedVecs.length ? Math.max(...c.dislikedVecs.map((v) => vecSim(f.v, v))) : 0;
      feedback = clamp(0.5 + 0.5 * lv - 0.5 * dv, 0, 1);
    }
    const occasion = ctx.occasion ? dnaSimilarity(f, c, ctx.occasion).S : 0.5;
    const sea = season();
    const seasonFit = clamp(1 - Math.abs(f.v[I.warmth] - SEASON_WARMTH[sea]) / 8, 0, 1);
    let personality = 0.5;
    if (c.traits.length) {
      let s = 0, k = 0;
      c.traits.forEach((t) => { TRAITS[t].up.forEach((d) => { s += f.v[I[d]] / 10; k++; }); TRAITS[t].down.forEach((d) => { s += 1 - f.v[I[d]] / 10; k++; }); });
      personality = k ? s / k : 0.5;
    }
    const inventory = f.available ? 1 : 0;
    // discovery: movement along characteristics with little evidence, scaled by adventurousness
    let nov = 0, kk = 0;
    for (let i = 0; i < 20; i++) if (c.evidence[i] < 0.6) { nov += Math.abs(f.v[i] - c.p[i]) / 10; kk++; }
    const discovery = clamp((kk ? nov / kk : 0) * 2 * (c.adventure / 10) + 0.2 * (f.v[I.uniqueness] / 10), 0, 1);
    const comp = { dna: sim.S, explicit, feedback, occasion, season: seasonFit, personality, inventory, discovery };
    const R = Object.keys(W).reduce((a, k) => a + W[k] * comp[k], 0);
    return { comp, R, likedHits };
  }

  const toPct = (R) => Math.min(98, Math.round(100 * (0.55 + 0.43 * Math.pow(clamp(R, 0, 1), 1.2))));

  /* ---------------- 9. Explanations (only our own data) ---------------- */
  function explain(f, c, sim, slot, likedHits) {
    const ok = sim.contrib.filter((x) => x.W >= 0.9 && x.gap <= c.sig[x.i] && (!f.cd || f.cd[x.i] >= 0.4));
    const highs = ok.filter((x) => x.mode !== 'max' && x.f >= 5.5 && x.p >= 5.5).sort((a, b) => b.W * b.f - a.W * a.f).slice(0, 3);
    const lows = ok.filter((x) => x.mode === 'max' && x.p <= 4).sort((a, b) => b.W - a.W).slice(0, 1);
    const favours = highs.map((x) => ADJ[x.d]).concat(lows.map((x) => 'restrained ' + LABEL[x.d].toLowerCase()));
    const lead = {
      signature: 'Your strongest match.', safe: 'Very close to what you already love.', next: 'Close to your taste, with a twist.',
      discovery: 'A step somewhere new for you.', occasion: 'Picked for the occasion.', wildcard: 'A bold pick we think you might love.', more: ''
    }[slot] || '';
    let why = favours.length ? `Chosen because your profile favours ${list(favours)}` : 'Chosen from your answers';
    const facts = highs.slice(0, 2).map((x) => `${LABEL[x.d].toLowerCase()} ${Math.round(x.f)}/10`)
      .concat(lows.map((x) => `${LABEL[x.d].toLowerCase()} only ${Math.round(x.f)}/10`));
    if (facts.length) why += `: this one scores ${list(facts)}`;
    if (likedHits.length) why += `${facts.length ? ', with' : '. It has'} ${list(likedHits.slice(0, 2))}, which you said you like`;
    if (slot === 'discovery' || slot === 'wildcard') {
      const newDim = sim.contrib.filter((x) => x.i < 20 && c.evidence[x.i] < 0.6 && x.f >= 6).sort((a, b) => b.f - a.f)[0];
      if (newDim) why += `. It brings in more ${LABEL[newDim.d].toLowerCase()} than you've tried`;
    }
    return (lead ? lead + ' ' : '') + why + '.';
  }
  const list = (a) => (a.length <= 1 ? a.join('') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1]);

  /* ---------------- 10. Recommend: filters → score → slots ---------------- */
  const SLOT_LABEL = { signature: 'Your signature', safe: 'Your safe bet', next: 'Your next note', discovery: 'Your discovery', occasion: 'Your occasion', wildcard: 'Your wildcard', more: 'Also for you' };

  function recommend(cat, c, ctx) {
    ctx = Object.assign({ max: 7 }, ctx || {});
    const C = cfg();
    const pool = [];
    cat.list.forEach((f) => {
      if (!f.hasData) return;
      if (hardFiltered(f, c, ctx)) return;
      const sim = dnaSimilarity(f, c, null);
      const { comp, R, likedHits } = components(f, c, ctx, sim, C.weights);
      const nearLoved = c.lovedVecs.length ? Math.max(...c.lovedVecs.map((v) => vecSim(f.v, v))) : 0;
      pool.push({ f, sim, comp, R, likedHits, nearLoved });
    });
    pool.sort((a, b) => b.R - a.R);

    const chosen = [];
    const far = (x) => chosen.every((y) => {
      let s = 0;
      for (let i = 0; i < 20; i++) { const d = x.f.v[i] - y.f.v[i]; s += d * d; }
      return Math.sqrt(s) >= 2.0;
    });
    const mmr = (x) => 0.7 * x.R - 0.3 * (chosen.length ? Math.max(...chosen.map((y) => vecSim(x.f.v, y.f.v))) : 0);
    const take = (slot, test) => {
      for (let relax = 0; relax < 4; relax++) {
        const cands = pool.filter((x) => !chosen.includes(x) && far(x) && test(x, relax * 0.08));
        if (cands.length) { cands.sort((a, b) => mmr(b) - mmr(a)); const pick = cands[0]; pick.slot = slot; chosen.push(pick); return; }
      }
    };
    const S = (x) => x.sim.S;
    take('signature', (x, r) => S(x) >= 0.8 - r);
    if (c.lovedVecs.length) take('safe', (x, r) => x.nearLoved >= 0.85 - r);
    else take('safe', (x, r) => S(x) >= 0.75 - r);
    take('next', (x, r) => S(x) >= 0.7 - r && S(x) <= 0.85 + r);
    if (ctx.occasion) {
      const oc = pool.filter((x) => !chosen.includes(x)).sort((a, b) => b.comp.occasion - a.comp.occasion)[0];
      if (oc) { oc.slot = 'occasion'; chosen.push(oc); }
    }
    if (c.adventure >= 4) take('discovery', (x, r) => S(x) >= 0.55 - r && S(x) <= 0.72 + r);
    if (c.adventure >= 6) take('wildcard', (x, r) => S(x) >= 0.45 - r && S(x) <= 0.65 + r && x.f.v[I.uniqueness] >= 6 - r * 10);
    while (chosen.length < ctx.max) {
      const rest = pool.filter((x) => !chosen.includes(x));
      if (!rest.length) break;
      rest.sort((a, b) => mmr(b) - mmr(a));
      rest[0].slot = 'more';
      chosen.push(rest[0]);
    }
    const ordered = ['occasion', 'signature', 'safe', 'next', 'discovery', 'wildcard', 'more'];
    if (!ctx.occasion) ordered.push(ordered.shift());
    chosen.sort((a, b) => ordered.indexOf(a.slot) - ordered.indexOf(b.slot) || b.R - a.R);
    const picks = chosen.slice(0, ctx.max).map((x) => ({
      product: x.f, slot: x.slot, label: SLOT_LABEL[x.slot], pct: toPct(x.R), R: x.R, S: x.sim.S,
      components: x.comp, reason: explain(x.f, c, x.sim, x.slot, x.likedHits)
    }));
    return { analysed: cat.analysed, considered: pool.length, picks };
  }

  /* ---------------- 11. Describe a customer ---------------- */
  function topTraits(c, n) {
    return DIMS.slice(0, 25).map((d, i) => ({ d, label: LABEL[d], score: c.p[i], weight: c.w[i] * Math.abs(c.p[i] - 5) }))
      .filter((x) => x.score >= 6).sort((a, b) => b.weight - a.weight).slice(0, n || 5);
  }
  function lowTraits(c, n) {
    return DIMS.slice(0, 23).map((d, i) => ({ d, label: LABEL[d], score: c.p[i], weight: c.w[i] * (5 - c.p[i]) }))
      .filter((x) => x.score <= 3.5 && (c.mode[DIMS.indexOf(x.d)] === 'max' || c.w[DIMS.indexOf(x.d)] >= 1.2)).sort((a, b) => b.weight - a.weight).slice(0, n || 3);
  }
  function describe(c) {
    const hi = topTraits(c, 6).filter((x) => x.d !== 'longevity' && x.d !== 'projection').slice(0, 3).map((x) => ADJ[x.d]);
    const lo = lowTraits(c, 2).map((x) => x.label.toLowerCase());
    const L = c.p[I.longevity], P = c.p[I.projection];
    let s = hi.length ? `Your profile leans toward ${list(hi)} fragrances` : 'Your profile is still taking shape';
    if (lo.length) s += `, with ${list(lo)} kept low`;
    if (L >= 7) s += ' and strong longevity';
    if (P >= 7) s += ', projecting with presence';
    else if (P <= 3.5) s += ', worn close to the skin';
    return s + '.';
  }

  /* ---------------- 12. Confidence ---------------- */
  function confidence(c, state) {
    const coverage = c.evidence.filter((e) => e >= 0.5).length / N;
    let consistency = 0.6;
    if (c.lovedVecs.length >= 2) {
      let tot = 0;
      for (let i = 0; i < 20; i++) {
        const xs = c.lovedVecs.map((v) => v[i]);
        const m = xs.reduce((a, b) => a + b, 0) / xs.length;
        tot += Math.sqrt(xs.reduce((a, b) => a + (b - m) * (b - m), 0) / xs.length);
      }
      consistency = clamp(1 - tot / 20 / 4, 0, 1);
    }
    const rated = (state.ratings || []).filter((r) => r.predicted != null);
    const volume = Math.min(1, (state.ratings || []).length / 20);
    let accuracy = 0.5;
    if (rated.length >= 3) {
      const last = rated.slice(-10);
      const rho = spearman(last.map((r) => r.predicted), last.map((r) => RATING[r.rating] || 0));
      accuracy = (rho + 1) / 2;
    }
    return Math.round(100 * (0.25 * coverage + 0.25 * consistency + 0.2 * volume + 0.3 * accuracy));
  }
  function spearman(a, b) {
    const rank = (x) => { const s = x.map((v, i) => [v, i]).sort((p, q) => p[0] - q[0]); const r = []; s.forEach(([, i], k) => (r[i] = k)); return r; };
    const ra = rank(a), rb = rank(b), n = a.length;
    const d2 = ra.reduce((s, v, i) => s + (v - rb[i]) * (v - rb[i]), 0);
    return n > 1 ? 1 - (6 * d2) / (n * (n * n - 1)) : 0;
  }
  const stageLine = (conf) => (conf >= 80 ? 'We know your fragrance.' : conf >= 55 ? "We're learning your preferences." : "We think you'll like this.");

  /* ---------------- 13. Saved state (this device) ---------------- */
  const stateKey = () => '5n_dna_state_v1' + (cfg().customerId ? '_' + cfg().customerId : '');
  function load() { try { return JSON.parse(localStorage.getItem(stateKey()) || 'null'); } catch (_) { return null; } }
  function save(state) { try { localStorage.setItem(stateKey(), JSON.stringify(state)); } catch (_) {} return state; }

  function snapshot(state, cat, reason) {
    const c = buildCustomer(state, cat);
    const prev = (state.history || [])[state.history ? state.history.length - 1 : -1];
    const changes = [];
    if (prev) DIMS.forEach((d, i) => { if (Math.abs(prev.p[i] - c.p[i]) > 0.2) changes.push({ d: LABEL[d], from: +prev.p[i].toFixed(1), to: +c.p[i].toFixed(1) }); });
    const conf = confidence(c, state);
    state.history = (state.history || []).concat([{ at: new Date().toISOString(), p: c.p.map((x) => +x.toFixed(2)), conf, reason, changes }]).slice(-36);
    state.confidence = conf;
    return { c, changes, conf };
  }

  function saveAnswers(answers, cat) {
    const state = load() || { created: new Date().toISOString(), ratings: [], excluded: [], history: [] };
    if (state.answers) state.initial = state.initial || state.answers;
    state.answers = answers;
    const r = snapshot(state, cat, 'Questionnaire');
    save(state);
    return r;
  }

  function addRatings(ratings, cat) {
    const state = load();
    if (!state) return null;
    const before = buildCustomer(state, cat);
    ratings.forEach((r) => {
      const f = cat.byHandle[r.handle];
      if (f) {
        const sim = dnaSimilarity(f, before, null);
        r.predicted = components(f, before, {}, sim).R;
      }
      r.at = new Date().toISOString();
      state.ratings.push(r);
      if (r.rating === 'hated') state.excluded = Array.from(new Set((state.excluded || []).concat([r.handle])));
    });
    const res = snapshot(state, cat, 'Ratings: ' + ratings.map((r) => r.title + ' (' + r.rating.replace(/_/g, ' ') + ')').join(', '));
    save(state);
    return res;
  }

  function summaryForSheet(c, state) {
    return {
      dna: DIMS.map((d, i) => `${d}:${c.p[i].toFixed(2)}`).join(' '),
      top_dna: topTraits(c, 5).map((x) => `${x.label} ${x.score.toFixed(1)}`).join(', '),
      low_dna: lowTraits(c, 3).map((x) => `${x.label} ${x.score.toFixed(1)}`).join(', '),
      hard_nos: c.hardNotes.concat(Object.keys(c.caps).map((d) => LABEL[d] + ' (cap ' + c.caps[d] + ')')).join(', '),
      traits: c.traits.join(', '),
      confidence: state.confidence != null ? state.confidence + '%' : '',
      profile: describe(c)
    };
  }

  async function postSheet(payload) {
    const C = cfg();
    if (!C.sheetUrl) return;
    try {
      await fetch(C.sheetUrl, { method: 'POST', mode: 'no-cors', keepalive: true, headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: JSON.stringify(Object.assign({ key: C.sheetKey }, payload)) });
    } catch (_) {}
  }

  /* Optional: your own server that asks Claude to rewrite the reasons. It must answer
     {"reasons":{"<handle>":"text"}} and may only use the data sent. */
  async function aiReasons(c, picks) {
    const url = cfg().aiUrl;
    if (!url || !picks.length) return null;
    const payload = {
      customer: { profile: describe(c), top: topTraits(c, 6), low: lowTraits(c, 3), hard_nos: c.hardNotes, traits: c.traits },
      candidates: picks.map((x) => ({
        handle: x.product.handle, title: x.product.title, brand: x.product.vendor, slot: x.slot, match_pct: x.pct,
        dna: Object.fromEntries(DIMS.map((d, i) => [d, +x.product.v[i].toFixed(1)])),
        notes: { top: x.product.notes_top || 'DATA_NOT_AVAILABLE', heart: x.product.notes_heart || 'DATA_NOT_AVAILABLE', base: x.product.notes_base || 'DATA_NOT_AVAILABLE' },
        components: x.components
      }))
    };
    try {
      const ctrl = new AbortController();
      setTimeout(() => ctrl.abort(), 8000);
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal: ctrl.signal });
      const data = await res.json();
      const out = {};
      Object.keys(data.reasons || {}).forEach((h) => {
        if (picks.some((x) => x.product.handle === h) && typeof data.reasons[h] === 'string') out[h] = data.reasons[h].slice(0, 400);
      });
      return out;
    } catch (_) { return null; }
  }

  /* ---------------- 14. Typeahead over the catalogue ---------------- */
  function typeahead(input, onPick) {
    const box = document.createElement('div');
    box.className = 'ta';
    box.setAttribute('role', 'listbox');
    box.hidden = true;
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.insertAdjacentElement('afterend', box);
    let items = [], active = -1;
    const close = () => { box.hidden = true; input.setAttribute('aria-expanded', 'false'); active = -1; };
    const render = async () => {
      const q = norm(input.value);
      if (q.length < 2) return close();
      const cat = await loadCatalogue();
      const words = q.split(' ');
      items = cat.list.filter((p) => { const t = norm(p.vendor + ' ' + p.title); return words.every((w) => t.includes(w)); }).slice(0, 8);
      box.innerHTML = '';
      items.forEach((p, k) => {
        const o = document.createElement('div');
        o.className = 'ta__opt'; o.setAttribute('role', 'option'); o.id = input.name + '-opt-' + k;
        o.textContent = (p.vendor ? p.vendor + ' — ' : '') + p.title;
        o.addEventListener('mousedown', (e) => { e.preventDefault(); onPick(p); input.value = ''; close(); });
        box.appendChild(o);
      });
      if (!items.length) { const o = document.createElement('div'); o.className = 'ta__empty'; o.textContent = 'Not in our catalogue yet. Add it below instead.'; box.appendChild(o); }
      box.style.top = (input.offsetTop + input.offsetHeight) + 'px';
      box.hidden = false; input.setAttribute('aria-expanded', 'true');
    };
    let t;
    input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(render, 150); });
    input.addEventListener('blur', () => setTimeout(close, 120));
    input.addEventListener('keydown', (e) => {
      if (box.hidden || !items.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        active = (active + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        [...box.children].forEach((el, k) => el.classList.toggle('is-active', k === active));
        input.setAttribute('aria-activedescendant', input.name + '-opt-' + active);
      } else if (e.key === 'Enter' && active >= 0) {
        e.preventDefault(); e.stopPropagation(); onPick(items[active]); input.value = ''; close();
      } else if (e.key === 'Escape') close();
    });
  }

  window.FifthDNA = {
    DIMS, LABEL, OCCASIONS, TRAITS, NOTE_MAP, loadCatalogue, prepareProduct, buildCustomer, recommend, dnaSimilarity, components, toPct,
    describe, topTraits, lowTraits, confidence, stageLine, load, save, saveAnswers, addRatings, summaryForSheet, postSheet, aiReasons, typeahead, season
  };
})();
